import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Security() {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation({ threshold: 0.15 });
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });

  const features = [
    { icon: 'ri-lock-password-line', title: 'End-to-End Encryption', desc: 'All data is encrypted using AES-256 encryption standards, ensuring your information remains secure from unauthorized access at every stage of transmission and storage.' },
    { icon: 'ri-shield-keyhole-line', title: 'Multi-Factor Authentication', desc: 'Add an extra layer of security with customizable MFA options including biometric authentication, SMS verification, and authenticator apps.' },
    { icon: 'ri-eye-line', title: 'Real-Time Threat Detection', desc: 'Advanced AI-powered monitoring systems detect and respond to potential security threats in real-time, protecting your data 24/7.' },
    { icon: 'ri-file-shield-2-line', title: 'Compliance & Certifications', desc: 'Fully compliant with GDPR, SOC 2, ISO 27001, and HIPAA standards, ensuring your business meets all regulatory requirements.' },
  ];

  const bottomStats = [
    { icon: 'ri-time-line', value: '99.9%', label: 'Uptime Guarantee' },
    { icon: 'ri-shield-check-line', value: 'Zero', label: 'Security Breaches' },
    { icon: 'ri-customer-service-2-line', value: '24/7', label: 'Security Support' },
  ];

  return (
    <section id="security" className="py-24 bg-gray-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-16 relative z-10">
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-6">
            <i className="ri-shield-check-line text-teal-400"></i>
            <span className="text-teal-400 text-sm font-semibold">Enterprise Security</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
            Advanced Security Protocols
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Protect your business with military-grade encryption and comprehensive security measures designed for enterprise-level protection
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className="space-y-8"
          >
            {features.map((item, i) => (
              <div
                key={i}
                className={`flex gap-4 transition-all duration-600 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
                style={{ transitionDelay: `${i * 130}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-xl border border-teal-500/30 flex-shrink-0">
                  <i className={`${item.icon} text-xl text-teal-400`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`relative transition-all duration-700 delay-200 ${rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
          >
            <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8 backdrop-blur-sm">
              <div className="space-y-6">
                {[
                  { label: 'Firewall Protection', sub: 'Active' },
                  { label: 'SSL Certificate', sub: 'Valid until 2025' },
                  { label: 'Data Backup', sub: 'Last backup: 2 hours ago' },
                  { label: 'Intrusion Detection', sub: 'Monitoring' },
                ].map((item, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between p-4 bg-gray-900/50 rounded-xl border border-gray-700 transition-all duration-500 ${rightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${300 + i * 100}ms` }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-green-500/20 rounded-lg border border-green-500/30">
                        <i className="ri-checkbox-circle-line text-green-400"></i>
                      </div>
                      <div>
                        <p className="text-white font-semibold">{item.label}</p>
                        <p className="text-sm text-gray-400">{item.sub}</p>
                      </div>
                    </div>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                ))}
              </div>

              <div className={`mt-8 p-6 bg-teal-500/10 border border-teal-500/30 rounded-xl transition-all duration-700 ${rightVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: '700ms' }}>
                <div className="flex items-center gap-3 mb-3">
                  <i className="ri-shield-star-line text-2xl text-teal-400"></i>
                  <h4 className="text-lg font-bold text-white">Security Score</h4>
                </div>
                <div className="flex items-end gap-2 mb-2">
                  <span className="text-4xl font-bold text-teal-400">98</span>
                  <span className="text-gray-400 mb-1">/100</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-teal-400 to-cyan-500 h-2 rounded-full" style={{ width: '98%' }}></div>
                </div>
              </div>
            </div>

            <div className="absolute -top-6 -right-6 w-32 h-32 bg-teal-500/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-cyan-500/20 rounded-full blur-3xl"></div>
          </div>
        </div>

        <div
          ref={statsRef as React.RefObject<HTMLDivElement>}
          className="grid md:grid-cols-3 gap-8"
        >
          {bottomStats.map((stat, i) => (
            <div
              key={i}
              className={`bg-gray-800/30 border border-gray-700 rounded-xl p-6 text-center transition-all duration-600 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="w-16 h-16 flex items-center justify-center bg-teal-500/20 rounded-full mx-auto mb-4 border border-teal-500/30">
                <i className={`${stat.icon} text-2xl text-teal-400`}></i>
              </div>
              <h4 className="text-2xl font-bold text-white mb-2">{stat.value}</h4>
              <p className="text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
